import moment from 'moment';
import { useIntl } from 'react-intl';
import { Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { getEventCategoryIcon } from '@/components/doohReport/common/helpers/eventCategoryIcon.helpers';
import { dayTimestamp } from '../dayTimestamp.helpers';
import { groupDatesIntoWeeks } from '../weekGroups.helpers';

import type { DoohReportEvent } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './eventsTrack.scss';

type PlotBox = {
  left: number;
  width: number;
  /** Each rendered day's actual pixel center within the plot area, keyed by its UTC start-of-day timestamp. */
  xByTimestamp: Record<string, number>;
};

type Props = {
  /** Every day the chart plots, in order — one column per date, same as the chart's x-axis. */
  dates: string[];
  events: DoohReportEvent[];
  /** The Highcharts plot area's own geometry, so the columns line up under the chart's actual day ticks. */
  plotBox: PlotBox | null;
};

type Slot = { left: number; width: number };

/** Visual gap between one week's line and the next, inset from each line's own slot so it never eats into the slot's real boundaries. */
const WEEK_LINE_INSET_PX = 4;
/** Comfortably fits a 2-line stacked label ("Jul" / "16") — below this, every other day's label is dropped instead of letting them overlap. */
const MIN_DAY_LABEL_WIDTH_PX = 20;

/** 1 shows every day's label, 2 every other, 3 every third, and so on — never more crowded than `MIN_DAY_LABEL_WIDTH_PX` allows. */
function dayLabelStep(dates: string[], plotBox: PlotBox): number {
  if (dates.length === 0) {
    return 1;
  }
  const averageDayWidth = plotBox.width / dates.length;
  return Math.max(1, Math.ceil(MIN_DAY_LABEL_WIDTH_PX / averageDayWidth));
}

/** `dates` is a plain `YYYY-MM-DD`, but the study's own event date carries a time too (e.g. "2026-07-18 19:00:00.000"). */
function dateKey(date: string): string {
  return moment.utc(date).format('YYYY-MM-DD');
}

function groupEventsByDate(events: DoohReportEvent[]): Map<string, DoohReportEvent[]> {
  const byDate = new Map<string, DoohReportEvent[]>();
  for (const event of events) {
    if (!event.startDate) {
      continue;
    }
    const key = dateKey(event.startDate);
    byDate.set(key, [...(byDate.get(key) ?? []), event]);
  }
  return byDate;
}

/**
 * One slot per day, its boundary sitting halfway between that day's actual
 * pixel center and its neighbors' — evenly dividing the plot width by day
 * count doesn't work here, since Highcharts doesn't necessarily space columns
 * evenly (axis padding, plot bands). Each day's real bar is always inside its
 * own slot by construction, which is what keeps the label/icon underneath it.
 */
function buildDaySlots(dates: string[], plotBox: PlotBox): Slot[] {
  const centers = dates.map(date => plotBox.xByTimestamp[dayTimestamp(date)] ?? null);
  const boundaries = [0];
  for (let i = 1; i < centers.length; i += 1) {
    const previous = centers[i - 1];
    const current = centers[i];
    boundaries.push(previous !== null && current !== null ? (previous + current) / 2 : boundaries[i - 1]);
  }
  boundaries.push(plotBox.width);

  return dates.map((_, index) => ({ left: boundaries[index], width: boundaries[index + 1] - boundaries[index] }));
}

/**
 * Real-world events rendered in a track parallel to the coverage/frequency
 * chart, aligned by date instead of embedded as plot lines inside it
 * (DOOH-564). Plain HTML/CSS — no Highcharts dependency here; `plotBox` is the
 * only thing borrowed from the chart, computed by the parent from its own
 * Highcharts instance.
 */
export default function EventsTrack({ dates, events, plotBox }: Readonly<Props>) {
  const { formatDate, formatMessage } = useIntl();

  if (dates.length === 0 || !plotBox) {
    return null;
  }

  const eventsByDate = groupEventsByDate(events);
  const weeks = groupDatesIntoWeeks(dates);
  const daySlots = buildDaySlots(dates, plotBox);
  const labelStep = dayLabelStep(dates, plotBox);
  // A week's slot spans exactly its member days' slots combined, so it groups
  // the same columns the day rows line up with.
  const weekSlots: Slot[] = [];
  let dayIndex = 0;
  for (const week of weeks) {
    const first = daySlots[dayIndex];
    const last = daySlots[dayIndex + week.dates.length - 1];
    weekSlots.push({ left: first.left, width: last.left + last.width - first.left });
    dayIndex += week.dates.length;
  }

  return (
    <div className="eventsTrack" style={{ marginLeft: plotBox.left, width: plotBox.width }}>
      <div className="eventsTrack__weekLines">
        {weeks.map((week, index) => (
          <span
            className="eventsTrack__weekLine"
            key={week.dates[0]}
            style={{
              left: weekSlots[index].left + WEEK_LINE_INSET_PX,
              width: Math.max(weekSlots[index].width - WEEK_LINE_INSET_PX * 2, 0)
            }}
          />
        ))}
      </div>
      <div className="eventsTrack__dayLabels">
        {dates.map(
          (date, index) =>
            index % labelStep === 0 && (
              <div
                className="eventsTrack__dayLabel"
                key={date}
                style={{ left: daySlots[index].left, width: daySlots[index].width }}
              >
                <span>{moment.utc(date).format('MMM')}</span>
                <span>{moment.utc(date).format('D')}</span>
              </div>
            )
        )}
      </div>
      <div className="eventsTrack__days">
        {dates.map((date, index) => {
          const dayEvents = eventsByDate.get(date) ?? [];
          const [firstEvent] = dayEvents;

          return (
            <div
              className={['eventsTrack__day', dayEvents.length > 1 && 'eventsTrack__day--stacked']
                .filter(Boolean)
                .join(' ')}
              key={date}
              style={{ left: daySlots[index].left, width: daySlots[index].width }}
            >
              {firstEvent && (
                <Tooltip
                  config={{
                    component: (
                      <div className="eventsTrack__tooltip">
                        {dayEvents.map(event => (
                          <div className="eventsTrack__tooltipEvent" key={event.id}>
                            <div
                              className={[
                                'eventsTrack__tooltipHeader',
                                !event.locationLabel && 'eventsTrack__tooltipHeader--centered'
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <span className="eventsTrack__tooltipEventName">
                                {event.name ??
                                  formatMessage({
                                    defaultMessage: 'Untitled event',
                                    id: 'doohReport.coverageFrequencyChart.eventsTrack.untitled'
                                  })}
                              </span>
                              {event.locationLabel && (
                                <span className="eventsTrack__tooltipEventAddress">{event.locationLabel}</span>
                              )}
                            </div>
                            {event.startDate && (
                              <>
                                <span className="eventsTrack__tooltipDivider" />
                                <span className="eventsTrack__tooltipEventDate">
                                  {formatDate(moment.utc(event.startDate).toDate(), {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </span>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ),
                    interactions: [TooltipInteractionEnum.hover],
                    placement: 'top',
                    strategy: 'absolute'
                  }}
                >
                  <span className={`eventsTrack__icon ${getEventCategoryIcon(firstEvent.category)}`} />
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
